import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Heart, ChevronRight, Columns, LayoutGrid, MessageCircle,
  Search, ArrowUpRight, Check, Zap,
} from "lucide-react";
import { useRole } from "../context/RoleContext";
import { listSavedDesigns, toggleSavedDesign } from "../lib/ownerPortal";
import DesignCard from "../components/DesignCard";
import {
  formatInt, formatPriceCompact, formatTypology, assetUrl,
} from "../lib/format";

/* ──────────────────────────────────────────────────────────────
   Filter chips driven by the actual saved corpus
   ────────────────────────────────────────────────────────────── */

function uniqueTypologies(items) {
  const set = new Set();
  for (const d of items) if (d.typology) set.add(d.typology);
  return [...set];
}

/* ──────────────────────────────────────────────────────────────
   Page
   ────────────────────────────────────────────────────────────── */

export default function SavedDesigns() {
  const { user, refreshUser } = useRole();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const [view, setView] = useState("grid"); // grid | compare
  const [typologyFilter, setTypologyFilter] = useState("all");
  const [sortBy, setSortBy] = useState("recent"); // recent | price-asc | price-desc | sqft

  useEffect(() => {
    if (!user?.id) return;
    let alive = true;
    listSavedDesigns(user.id).then((d) => alive && setItems(d.items || []));
    return () => { alive = false; };
  }, [user?.id]);

  const unsave = async (designId, designName) => {
    setBusy(designId);
    try {
      await toggleSavedDesign(user.id, designId);
      const next = await listSavedDesigns(user.id);
      setItems(next.items || []);
      refreshUser();
      toast.success(`${designName || "Design"} removed from shortlist.`);
    } finally {
      setBusy(null);
    }
  };

  /* ─── derived ─── */
  const portfolio = useMemo(() => {
    if (!items || items.length === 0) return null;
    const prices = items.map((d) => d.all_in_price || 0).filter(Boolean);
    return {
      count: items.length,
      min: prices.length ? Math.min(...prices) : 0,
      max: prices.length ? Math.max(...prices) : 0,
      typologies: uniqueTypologies(items),
      preReviewed: items.filter((d) => d.status === "Approved").length,
      inMotion: items.filter((d) => d.in_motion_project).length,
    };
  }, [items]);

  const visible = useMemo(() => {
    if (!items) return [];
    let out = items;
    if (typologyFilter !== "all") {
      out = out.filter((d) => d.typology === typologyFilter);
    }
    if (sortBy === "price-asc")
      out = [...out].sort((a, b) => (a.all_in_price || 0) - (b.all_in_price || 0));
    else if (sortBy === "price-desc")
      out = [...out].sort((a, b) => (b.all_in_price || 0) - (a.all_in_price || 0));
    else if (sortBy === "sqft")
      out = [...out].sort((a, b) => (b.sqft || 0) - (a.sqft || 0));
    return out;
  }, [items, typologyFilter, sortBy]);

  /* ─── header ─── */
  return (
    <section className="animate-fade-in-up" data-testid="owner-saved">
      <div className="flex items-center gap-3">
        <span aria-hidden style={{ display: "inline-block", width: 28, height: 2, background: "#B14E2F" }} />
        <span className="wn-label" style={{ color: "#8F3C23" }}>
          Owner shortlist
        </span>
      </div>
      <h1
        className="mt-3"
        style={{
          fontFamily: '"Fraunces", Georgia, serif',
          fontSize: "clamp(28px, 4vw, 40px)",
          fontWeight: 500,
          color: "#1B1F4B",
          lineHeight: 1.08,
          letterSpacing: "-0.01em",
          margin: 0,
        }}
      >
        Your shortlist.
      </h1>
      <p
        className="mt-2"
        style={{
          fontFamily: '"Newsreader", Georgia, serif',
          fontStyle: "italic",
          color: "#6C6B85",
          fontSize: 16,
          lineHeight: 1.5,
          maxWidth: 720,
          margin: 0,
        }}
      >
        Designs you're considering for your block — pre-reviewed by City
        Staff, stamped by an architect, ready to assign to a parcel.
      </p>

      {/* Stat strip */}
      {portfolio && <ShortlistStats portfolio={portfolio} />}

      {/* Filters + view toggle */}
      {items && items.length > 0 && (
        <Controls
          items={items}
          typologyFilter={typologyFilter}
          setTypologyFilter={setTypologyFilter}
          sortBy={sortBy}
          setSortBy={setSortBy}
          view={view}
          setView={setView}
        />
      )}

      {items === null && (
        <div className="mt-8" style={{ color: "#6C6B85", fontStyle: "italic" }}>
          Loading saved designs…
        </div>
      )}

      {items && items.length === 0 && <EmptyState />}

      {items && items.length > 0 && view === "grid" && (
        <div
          className="mt-6 grid gap-6"
          style={{
            // Cap columns to the number of saved items (max 3) so 1 or 2
            // designs don't stretch into awkward jumbo cards. We keep the
            // 320px min so the page still wraps gracefully on tablet.
            gridTemplateColumns:
              `repeat(${Math.min(3, visible.length)}, minmax(320px, 1fr))`,
            // For 1–2 cards, cap the row width so cards stay card-shaped
            // (the full-width image strip becomes unwieldy past ~480px).
            maxWidth:
              visible.length === 1 ? 460
              : visible.length === 2 ? 940
              : "none",
          }}
          data-testid="owner-saved-grid"
        >
          {visible.map((d) => (
            <ShortlistCard
              key={d.id}
              d={d}
              busy={busy === d.id}
              onUnsave={() => unsave(d.id, d.name)}
            />
          ))}
        </div>
      )}

      {items && items.length > 0 && view === "compare" && (
        <CompareTable items={visible} onUnsave={unsave} busy={busy} />
      )}
    </section>
  );
}

/* ──────────────── Portfolio stats ──────────────── */

function ShortlistStats({ portfolio }) {
  return (
    <div
      className="mt-6 grid grid-cols-2 md:grid-cols-4"
      style={{
        gap: 0,
        borderTop: "1px solid #D9D8E4",
        borderBottom: "1px solid #D9D8E4",
      }}
    >
      <Stat label="Saved" value={portfolio.count} />
      <Stat
        label="Estimated all-in range"
        value={`${formatPriceCompact(portfolio.min)} — ${formatPriceCompact(portfolio.max)}`}
        accent
      />
      <Stat label="Pre-reviewed" value={portfolio.preReviewed} />
      <Stat label="Already in motion" value={portfolio.inMotion} />
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div style={{ padding: "16px 18px", borderRight: "1px solid #D9D8E4" }}>
      <div className="wn-label" style={{ color: "#6C6B85" }}>{label}</div>
      <div
        className="tabular-nums mt-1"
        style={{
          fontFamily: '"Nunito Sans", sans-serif',
          fontWeight: 800,
          fontSize: 22,
          color: accent ? "#8F3C23" : "#1B1F4B",
          letterSpacing: "-0.005em",
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
    </div>
  );
}

/* ──────────────── Controls (filter + sort + view toggle) ──────────────── */

function Controls({ items, typologyFilter, setTypologyFilter, sortBy, setSortBy, view, setView }) {
  const typologies = uniqueTypologies(items);
  return (
    <div
      className="mt-6 flex items-center justify-between flex-wrap"
      style={{ gap: 12 }}
      data-testid="owner-saved-controls"
    >
      <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
        <Chip
          label={`All (${items.length})`}
          active={typologyFilter === "all"}
          onClick={() => setTypologyFilter("all")}
          testid="filter-all"
        />
        {typologies.map((t) => {
          const c = items.filter((d) => d.typology === t).length;
          return (
            <Chip
              key={t}
              label={`${formatTypology(t)} (${c})`}
              active={typologyFilter === t}
              onClick={() => setTypologyFilter(t)}
              testid={`filter-${t}`}
            />
          );
        })}
      </div>
      <div className="flex items-center" style={{ gap: 12 }}>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          data-testid="owner-saved-sort"
          style={{
            fontFamily: '"Nunito Sans", sans-serif',
            fontSize: 12.5,
            color: "#1B1F4B",
            border: "1px solid #D9D8E4",
            background: "#FBFAFD",
            padding: "6px 10px",
            borderRadius: 4,
            cursor: "pointer",
          }}
        >
          <option value="recent">Most recent</option>
          <option value="price-asc">Estimated all-in: low to high</option>
          <option value="price-desc">Estimated all-in: high to low</option>
          <option value="sqft">Largest first</option>
        </select>
        <div
          className="inline-flex"
          style={{
            border: "1px solid #D9D8E4",
            borderRadius: 4,
            overflow: "hidden",
          }}
          data-testid="owner-saved-view-toggle"
        >
          <ViewBtn label="Grid" icon={LayoutGrid} active={view === "grid"} onClick={() => setView("grid")} testid="view-grid" />
          <ViewBtn label="Compare" icon={Columns} active={view === "compare"} onClick={() => setView("compare")} testid="view-compare" />
        </div>
      </div>
    </div>
  );
}

function Chip({ label, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      style={{
        fontFamily: '"Nunito Sans", sans-serif',
        fontWeight: 700,
        fontSize: 12,
        letterSpacing: "0.04em",
        padding: "6px 12px",
        borderRadius: 999,
        border: active ? "1px solid #8F3C23" : "1px solid #D9D8E4",
        background: active ? "#8F3C23" : "transparent",
        color: active ? "#FBFAFD" : "#1B1F4B",
        cursor: "pointer",
        transition: "background-color 160ms ease, border-color 160ms ease",
      }}
    >
      {label}
    </button>
  );
}

function ViewBtn({ label, icon: Icon, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center"
      style={{
        gap: 6,
        fontFamily: '"Nunito Sans", sans-serif',
        fontWeight: 700,
        fontSize: 12,
        padding: "6px 12px",
        background: active ? "#1B1F4B" : "transparent",
        color: active ? "#FBFAFD" : "#1B1F4B",
        border: "none",
        cursor: "pointer",
      }}
    >
      <Icon size={12} /> {label}
    </button>
  );
}

/* ──────────────── Shortlist card (grid view) ──────────────── */

function ShortlistCard({ d, busy, onUnsave }) {
  return (
    <div className="relative" data-testid={`owner-saved-card-${d.id}`}>
      <DesignCard design={{ ...d, all_in_cost: d.all_in_price }} />
      {/* Status pill (top-left) — only when Approved */}
      {d.status === "Approved" && (
        <span
          className="absolute inline-flex items-center"
          style={{
            top: 12, left: 12,
            gap: 5,
            background: "#FBFAFD",
            border: "1px solid #BFCBBF",
            color: "#4F6149",
            fontFamily: '"Nunito Sans", sans-serif',
            fontWeight: 800,
            fontSize: 10.5,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            padding: "4px 8px",
            borderRadius: 999,
          }}
        >
          <Check size={10} strokeWidth={3} /> City reviewed
        </span>
      )}
      {/* Unsave heart (top-right) */}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onUnsave();
        }}
        disabled={busy}
        className="absolute inline-flex items-center justify-center"
        style={{
          top: 12, right: 12,
          width: 34, height: 34,
          background: "#FBFAFD",
          border: "1px solid #D9D8E4",
          borderRadius: 999,
          cursor: "pointer",
          color: "#B14E2F",
          transition: "border-color 160ms ease",
        }}
        title="Remove from shortlist"
        data-testid={`owner-unsave-${d.id}`}
        onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#B14E2F")}
        onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#D9D8E4")}
      >
        <Heart size={16} fill="#B14E2F" />
      </button>

      {/* Meta strip below the card (architect + in-motion + CTAs) */}
      <div className="mt-3" style={{ fontSize: 12.5 }}>
        {(d.architect_firm || d.architect_name) && (
          <div
            style={{
              fontFamily: '"Newsreader", Georgia, serif',
              fontStyle: "italic",
              color: "#6C6B85",
            }}
          >
            Stamped by{" "}
            <span style={{ color: "#272636" }}>
              {d.architect_firm || d.architect_name}
            </span>
          </div>
        )}
        {d.in_motion_project && (
          <Link
            to={`/owner/projects/${d.in_motion_project.id}`}
            data-testid={`saved-in-motion-${d.id}`}
            className="mt-2 inline-flex items-center"
            style={{
              gap: 6,
              padding: "4px 10px",
              borderRadius: 999,
              background: "#EEEEF7",
              border: "1px solid #C9C9E0",
              color: "#1B1F4B",
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 11.5,
              textDecoration: "none",
            }}
          >
            <Zap size={11} />
            In motion · {d.in_motion_project.current_stage}
            <ArrowUpRight size={11} />
          </Link>
        )}
        <div className="mt-3 flex flex-wrap" style={{ gap: 8 }}>
          <Link
            to={`/parcels?design=${d.id}`}
            className="inline-flex items-center"
            data-testid={`saved-find-parcel-${d.id}`}
            style={{
              gap: 5,
              padding: "6px 12px",
              borderRadius: 999,
              border: "1px solid #D9D8E4",
              color: "#1B1F4B",
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 11.5,
              textDecoration: "none",
              transition: "border-color 160ms ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#B14E2F")}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#D9D8E4")}
          >
            <Search size={11} /> Find parcel
          </Link>
          <Link
            to={`/marketplace/${d.id}`}
            className="inline-flex items-center"
            data-testid={`saved-request-${d.id}`}
            style={{
              gap: 5,
              padding: "6px 12px",
              borderRadius: 999,
              background: "#1B1F4B",
              color: "#FBFAFD",
              border: "1px solid #1B1F4B",
              fontFamily: '"Nunito Sans", sans-serif',
              fontWeight: 700,
              fontSize: 11.5,
              textDecoration: "none",
            }}
          >
            <MessageCircle size={11} /> Request consult
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ──────────────── Compare table view ──────────────── */

function CompareTable({ items, onUnsave, busy }) {
  if (items.length === 0) return null;
  const rows = [
    { label: "Architect", key: "architect_firm" },
    { label: "Typology", key: "typology", fmt: formatTypology },
    { label: "Style", key: "style" },
    { label: "Beds", key: "beds" },
    { label: "Baths", key: "baths" },
    {
      label: "Square footage",
      key: "sqft",
      fmt: (v) => v ? `${formatInt(v)} sqft` : "—",
    },
    {
      label: "Min lot width",
      key: "min_lot_width",
      fmt: (v) => v ? `${v} ft` : "—",
    },
    {
      label: "Estimated all-in",
      key: "all_in_price",
      fmt: (v) => v ? formatPriceCompact(v) : "—",
      accent: true,
    },
    {
      label: "City status",
      key: "status",
      fmt: (v) => v ? <span style={{ color: v === "Approved" ? "#4F6149" : "#6C6B85" }}>{v}</span> : "—",
    },
  ];
  return (
    <div className="mt-6 overflow-x-auto" data-testid="owner-saved-compare">
      <table
        style={{
          width: "100%",
          minWidth: 720,
          borderCollapse: "collapse",
          background: "#FBFAFD",
          border: "1px solid #D9D8E4",
          borderRadius: 4,
          overflow: "hidden",
        }}
      >
        <thead>
          <tr>
            <th
              style={{
                padding: "14px 16px",
                textAlign: "left",
                borderBottom: "1px solid #D9D8E4",
                fontFamily: '"Nunito Sans", sans-serif',
                fontWeight: 700,
                fontSize: 11.5,
                color: "#6C6B85",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                background: "#F5F4FA",
              }}
            >
              Attribute
            </th>
            {items.map((d) => (
              <th
                key={d.id}
                style={{
                  padding: "14px 16px",
                  textAlign: "left",
                  borderBottom: "1px solid #D9D8E4",
                  borderLeft: "1px solid #ECEBF1",
                  verticalAlign: "top",
                  minWidth: 200,
                  background: "#F5F4FA",
                }}
              >
                <Link
                  to={`/marketplace/${d.id}`}
                  style={{
                    fontFamily: '"Fraunces", Georgia, serif',
                    fontWeight: 600,
                    fontSize: 16,
                    color: "#1B1F4B",
                    textDecoration: "none",
                    display: "block",
                    lineHeight: 1.2,
                  }}
                >
                  {d.name}
                </Link>
                {d.in_motion_project && (
                  <Link
                    to={`/owner/projects/${d.in_motion_project.id}`}
                    className="mt-1 inline-flex items-center"
                    style={{
                      gap: 4,
                      fontSize: 11,
                      color: "#1B1F4B",
                      background: "#EEEEF7",
                      border: "1px solid #C9C9E0",
                      borderRadius: 999,
                      padding: "2px 8px",
                      fontFamily: '"Nunito Sans", sans-serif',
                      fontWeight: 700,
                      textDecoration: "none",
                    }}
                  >
                    <Zap size={10} /> In motion
                  </Link>
                )}
                <button
                  type="button"
                  onClick={() => onUnsave(d.id, d.name)}
                  disabled={busy === d.id}
                  className="mt-2 inline-flex items-center"
                  data-testid={`compare-unsave-${d.id}`}
                  style={{
                    gap: 4,
                    fontFamily: '"Nunito Sans", sans-serif',
                    fontWeight: 700,
                    fontSize: 11,
                    color: "#B14E2F",
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                  }}
                >
                  <Heart size={11} fill="#B14E2F" /> Remove
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.key}>
              <td
                style={{
                  padding: "12px 16px",
                  borderTop: i === 0 ? "none" : "1px solid #ECEBF1",
                  fontFamily: '"Nunito Sans", sans-serif',
                  fontWeight: 700,
                  fontSize: 12,
                  color: "#6C6B85",
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                  width: 180,
                }}
              >
                {r.label}
              </td>
              {items.map((d) => {
                const raw = d[r.key];
                const val = r.fmt ? r.fmt(raw) : (raw ?? "—");
                return (
                  <td
                    key={d.id}
                    style={{
                      padding: "12px 16px",
                      borderTop: i === 0 ? "none" : "1px solid #ECEBF1",
                      borderLeft: "1px solid #ECEBF1",
                      fontFamily: '"Nunito Sans", sans-serif',
                      fontSize: 13.5,
                      color: r.accent ? "#8F3C23" : "#272636",
                      fontWeight: r.accent ? 800 : 500,
                    }}
                  >
                    {val}
                  </td>
                );
              })}
            </tr>
          ))}
          {/* Actions row */}
          <tr>
            <td
              style={{
                padding: "12px 16px",
                borderTop: "1px solid #ECEBF1",
                fontFamily: '"Nunito Sans", sans-serif',
                fontWeight: 700,
                fontSize: 12,
                color: "#6C6B85",
                letterSpacing: "0.04em",
                textTransform: "uppercase",
              }}
            >
              Next step
            </td>
            {items.map((d) => (
              <td
                key={d.id}
                style={{
                  padding: "12px 16px",
                  borderTop: "1px solid #ECEBF1",
                  borderLeft: "1px solid #ECEBF1",
                }}
              >
                <div className="flex flex-wrap" style={{ gap: 6 }}>
                  <Link
                    to={`/parcels?design=${d.id}`}
                    className="inline-flex items-center"
                    style={{
                      gap: 4,
                      padding: "5px 10px",
                      borderRadius: 999,
                      border: "1px solid #D9D8E4",
                      color: "#1B1F4B",
                      fontFamily: '"Nunito Sans", sans-serif',
                      fontWeight: 700,
                      fontSize: 11,
                      textDecoration: "none",
                    }}
                  >
                    <Search size={10} /> Parcel
                  </Link>
                  <Link
                    to={`/marketplace/${d.id}`}
                    className="inline-flex items-center"
                    style={{
                      gap: 4,
                      padding: "5px 10px",
                      borderRadius: 999,
                      background: "#1B1F4B",
                      color: "#FBFAFD",
                      border: "1px solid #1B1F4B",
                      fontFamily: '"Nunito Sans", sans-serif',
                      fontWeight: 700,
                      fontSize: 11,
                      textDecoration: "none",
                    }}
                  >
                    <MessageCircle size={10} /> Consult
                  </Link>
                </div>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/* ──────────────── Empty state ──────────────── */

function EmptyState() {
  return (
    <div
      className="mt-10"
      data-testid="owner-saved-empty"
      style={{
        border: "1px solid #D9D8E4",
        borderRadius: 4,
        background: "#FBFAFD",
        padding: "28px 28px",
        display: "grid",
        gridTemplateColumns: "1fr auto",
        alignItems: "center",
        gap: 20,
      }}
    >
      <div>
        <div
          style={{
            fontFamily: '"Fraunces", Georgia, serif',
            fontWeight: 500,
            fontSize: 22,
            color: "#1B1F4B",
            lineHeight: 1.2,
          }}
        >
          Nothing here yet.
        </div>
        <div
          className="mt-2"
          style={{
            fontFamily: '"Newsreader", Georgia, serif',
            fontStyle: "italic",
            color: "#6C6B85",
            fontSize: 14,
          }}
        >
          Tap the heart on any approved design in the marketplace and it
          will land here for side-by-side comparison.
        </div>
      </div>
      <Link
        to="/marketplace"
        className="wn-btn wn-btn-accent inline-flex items-center"
        style={{ gap: 8 }}
        data-testid="saved-browse-btn"
      >
        Browse marketplace <ChevronRight size={14} />
      </Link>
    </div>
  );
}
